import { NavLink } from "react-router";
import { UUID } from "@bitory-gmbh/nidavellir";
import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb";
import t from "@/lang/lang";
import { HtzBreadcrumbs } from "@/routes/processes/[processId]/htz/_components/htz-breadcrumbs";
import { DocumentReferenceNumberById } from "@/services/backend/documents/components/document-reference-number-by-id";
import { ReferenceNumber } from "@/services/backend/documents/components/reference-number";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import { Summary } from "@/services/backend/htz/summary/types/summary";

/**
 * SummaryBreadcrumbs renders the breadcrumb trail below a summary:
 * Customer > Process > Inspektionsberichte > <summary reference number>.
 *
 * Pages nested in the summary pass the `reportId` of the Anlagenbericht they
 * show. The summary crumb then links back to its items and the report
 * becomes the current page: … > <summary number> > <report number>. Only
 * the id of the report is known here, so its number is loaded.
 */
export function SummaryBreadcrumbs({
  summary,
  reportId,
}: {
  summary: Summary;
  reportId?: UUID;
}) {
  const summariesPath = `/processes/htz/${summary.processId!}/summaries`;

  return (
    <HtzBreadcrumbs
      customerId={summary.customerId}
      processId={summary.processId!}
    >
      <BreadcrumbSeparator />
      <BreadcrumbItem className="hidden md:block">
        <BreadcrumbLink asChild>
          <NavLink to={summariesPath}>{t("Inspektionsberichte")}</NavLink>
        </BreadcrumbLink>
      </BreadcrumbItem>
      <BreadcrumbSeparator />
      {reportId ? (
        <>
          <BreadcrumbItem className="hidden md:block">
            <BreadcrumbLink asChild>
              <NavLink to={`${summariesPath}/${summary.id}/items`}>
                <ReferenceNumber referenceNumber={summary.referenceNumber} />
              </NavLink>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>
              <DocumentReferenceNumberById
                serviceKind={GSMServiceKind.HTZ}
                documentKind={GSMDocumentKind.Report}
                documentId={reportId}
              />
            </BreadcrumbPage>
          </BreadcrumbItem>
        </>
      ) : (
        <BreadcrumbItem>
          <BreadcrumbPage>
            <ReferenceNumber referenceNumber={summary.referenceNumber} />
          </BreadcrumbPage>
        </BreadcrumbItem>
      )}
    </HtzBreadcrumbs>
  );
}
